import { authNativeHandoffWebExchangeCreate } from "@sidegigllc/api/client";
import { ApiError } from "@sidegigllc/api/resources";
import Session from "supertokens-web-js/recipe/session";
import { captureException } from "utils/errorReporting";
import * as z from "zod/mini";

const handoffSchema = z.object({
  code: z.string().check(z.minLength(1)),
  codeVerifier: z.string().check(z.minLength(1)),
  userId: z.string().check(z.minLength(1)),
});

type NativeSessionHandoff = z.infer<typeof handoffSchema>;

// Shared by overlapping auth checks (preloads, quick navigations) so none reaches the session check mid-exchange.
let adoption: Promise<void> | undefined;

const SERVICE_UNAVAILABLE = 503;
// The API's Retry-After. Neither it nor the 503 body reaches us (no CORS expose; ApiError drops 5xx bodies), so a
// 503 that burned the code also gets one retry; that retry 400s into the normal login.
const RETRY_AFTER_MS = 5000;

const exchange = (code: string, codeVerifier: string) =>
  authNativeHandoffWebExchangeCreate({ code, code_verifier: codeVerifier }, { headers: { "st-auth-mode": "cookie" } });

function takeHandoff(): NativeSessionHandoff | undefined {
  const payload = globalThis.window?.__QWICK_NATIVE_HANDOFF__;
  if (payload === undefined) return;
  // The code is single-use: drop it before anything awaits so no later check can redeem it again.
  globalThis.window.__QWICK_NATIVE_HANDOFF__ = undefined;
  return handoffSchema.safeParse(payload).data;
}

async function redeem({ code, codeVerifier, userId }: NativeSessionHandoff) {
  try {
    if (await Session.doesSessionExist()) {
      // Native re-injects the same code on every reload; the session it already bought is still good.
      if ((await Session.getUserId()) === userId) return;
      // Someone else signed into the app on this device; don't leave the WebView on the previous user.
      await Session.signOut();
    }
    try {
      await exchange(code, codeVerifier);
    } catch (error) {
      if (ApiError.fromThrown(error).httpStatus !== SERVICE_UNAVAILABLE) throw error;
      await new Promise((resolve) => {
        setTimeout(resolve, RETRY_AFTER_MS);
      });
      await exchange(code, codeVerifier);
    }
  } catch (error) {
    // Falls through to the normal session check, so the user gets today's login instead.
    captureException(error);
  }
}

// Redeems the code the Pro app injects into its Team Captain WebView for a cookie session. Keyed off the
// payload, not `isNativeWebView()`: that WebView sets no `onMessage`, so it has no bridge. Never throws.
export function adoptNativeSession(): Promise<void> {
  const handoff = takeHandoff();
  if (handoff) adoption = redeem(handoff);
  return adoption ?? Promise.resolve();
}
